import React from "react";
import { Button } from "../../components/template";
import { ShareSection } from '../../components/template';

// Define hashtag groups for Blog2 (adjust as per your image)


const hashtagsBottom = [
  "#nutritionalpsychiatry",
  "#ayurvedicnutrition",
  "#mindfuleating",  "#proteinintake",
  "#foodmyths",
  "#cleaneating",  "#yogicfood",
  "#consciousfood",
  "#foodasmedicine",


];

// Reusable HashtagSection with consistent styling
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const BlogPost = () => {
  return (
    <div className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed">
      {/* Header Section */}
      <h1 className="text-secondary text-center text-3xl font-bold mb-8">
        “Bro, What’s Your Protein Intake?” — And Other Myths About Food
      </h1>
      <img
        src="/assets/knowyourself/blogs/blog2/b2p1.jpg"
        alt="Image of a man drinking protein shake"
        className="w-[80%] max-w-xl h-auto max-h-[300px] rounded-lg shadow-md mx-auto object-cover mb-8"
      />


      {/* Introduction */}
      <section className="space-y-4 mb-8">
        <p>Let’s start with a scene that’s all too familiar.</p>
        <p>
          You’re at a party. One guy is on keto, someone else is talking about “eating
          clean,” and another proudly announces, “I only do 90 grams of protein a day,
          bro.”
        </p>
        <p>
          Meanwhile, their mind is cluttered, their gut is grumpy, and their mood swings
          could rival a Bollywood villain. See the irony?
        </p>
        <p>
          We’ve reduced food — something once considered sacred — to a math problem.
          Calories, macros, grams, guilt.
        </p>
        <p>
          But here’s the uncomfortable truth: Most people today know how much protein is
          in a peanut. But have <strong>no idea</strong> how food impacts their{" "}
          <strong>mental, emotional, and spiritual well-being</strong>.
        </p>
      </section>

      {/* The Yogic Way: Food Beyond Fuel */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-3xl text-center font-semibold">
          The Yogic Way: Food Beyond Fuel
        </h2>
        <img
          src="/assets/knowyourself/blogs/blog2/b2p2.jpg"
          alt="Image of a balanced Indian meal"
          className="w-[80%] max-w-xl h-auto max-h-[400px] rounded-lg shadow-md mx-auto object-cover mb-8"
        />
        <p>
          In Indian tradition, food is not just energy — it’s <strong>consciousness</strong>.
        </p>
        <p>
          Food doesn’t just shape your body; it shapes your thoughts, your emotions, your
          tendencies, your sleep, your energy, your <em>destiny</em>.
        </p>
        <p>Yes, really.</p>
        <p>
          We say: <strong>“Jaisa Ann, Vaisa Mann.”</strong> <em>As the food, so the mind.</em>
        </p>
        <p>The Bhagavad Gita (6.17) gives it straight:</p>
        <p className="whitespace-pre-line text-[#578d48]">
          <strong>
            “Yuktāhāra-vihārasya yukta-cheṣṭasya karmasu,
            <br />
            Yukta-svapnāvabodhasya yogo bhavati duḥkha-hā.”
          </strong>
        </p>
        <p>
          <em>
            He who is temperate in eating, recreation, actions, sleep, and waking — such a
            yogi destroys all suffering through discipline.
          </em>
        </p>
      </section>

      {/* Shocking Reality: What Are We Really Eating? */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          Shocking Reality: What Are We Really Eating?
        </h2>
        <p>
          Let’s break it down. Today, more than <strong>90% of what we eat</strong> is either{" "}
          <strong>Rajasic or Tamasic</strong> — and no, this isn’t just spiritual jargon.
          These are energetic imprints in food that <strong>directly affect our psychology</strong>.
        </p>
        <h3 className="text-secondary font-semibold">
          🔹 Tamasic Food (Heavy, dull, lifeless):
        </h3>
        <ul className="list-disc list-inside space-y-1">
          <li>Processed junk</li>
          <li>Reheated leftovers</li>
          <li>Cold or canned foods</li>
          <li>Over-fermented foods</li>
          <li>Excess meat, alcohol, sugary desserts</li>
        </ul>
        <p>
          <strong>Result:</strong> Lethargy, depression, confusion, slow digestion, poor memory.
        </p>
        <h3 className="text-secondary font-semibold">
          🔸 Rajasic Food (Overstimulating, aggressive):
        </h3>
        <ul className="list-disc list-inside space-y-1">
          <li>Excessively spicy food</li>
          <li>Fried items</li>
          <li>Coffee, chocolate, soft drinks</li>
          <li>Garlic, onions (especially in excess)</li>
          <li>Restaurant meals high in oil/salt</li>
        </ul>
        <p>
          <strong>Result:</strong> Restlessness, anxiety, ambition, irritability, craving more.
        </p>
        <h3 className="text-[#578d48] font-semibold">
          🟢 Sattvic Food (Pure, balanced, light):
        </h3>
        <ul className="list-disc list-inside space-y-1">
          <li>Fresh fruits and vegetables</li>
          <li>Whole grains, nuts, seeds</li>
          <li>Cow milk (from an ethical source), ghee, honey</li>
          <li>Herbal teas, mildly spiced home-cooked food</li>
        </ul>
        <p>
          <strong>Result:</strong> Mental clarity, calmness, compassion, high energy, better sleep.
        </p>
        <p>
          Now ask yourself — <strong>what percentage of your daily food is truly sattvic?</strong>{" "}
          Hard-hitting, isn’t it?
        </p>
      </section>

      {/* Ayurveda: The Science of You */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-3xl text-center mt-2 font-semibold">
          Ayurveda: The Science of You
        </h2>
        <img
          src="/assets/knowyourself/blogs/blog2/b2p3.jpg"
          alt="Illustration of Ayurveda symbols"
          className="w-[80%] max-w-xl h-auto max-h-[250px] mx-auto object-cover mb-8"
        />
        <p>
          Why do some people thrive on raw salads while others feel bloated? Why does fasting
          energize your friend but makes you want to punch a wall? Because…{" "}
          <strong>one diet does not fit all</strong>.
        </p>
        <p>
          Ayurveda speaks of your <strong>Prakriti</strong> — your unique body-mind type:
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong className="text-[#578d48]">Vata (Air + Space)</strong> – Light, dry, cold. Needs
            grounding, warm, oily food.
          </li>
          <li>
            <strong className="text-[#578d48]">Pitta (Fire + Water)</strong> – Sharp, intense. Needs
            cooling, fresh, less spicy food.
          </li>
          <li>
            <strong className="text-[#578d48]">Kapha (Water + Earth)</strong> – Heavy, stable. Needs
            light, spicy, dry food.
          </li>
        </ul>
        <p>
          Then there’s <strong className="text-[#578d48]">Agni</strong> – your digestive fire.
          If it’s weak (<em>manda</em>), even salads can cause gas. If too sharp (<em>tikshna</em>), even healthy food feels like lava.
        </p>
        <p>
          And finally, the <strong className="text-[#578d48]">Gunas</strong> — the real influencers of the food world.
          Sattva uplifts. Rajas agitates. Tamas dulls.
        </p>
      </section>


      {/* Nutritional Psychiatry */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          The Science Is Catching Up — Say Hello to Nutritional Psychiatry
        </h2>
        <p>
          Once dismissed as folklore, <strong className="text-[#578d48]">“food affects mood”</strong> is now backed by
          heavy-duty research.
        </p>
        <p>
          <strong>Nutritional Psychiatry shows:</strong>
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>
            The brain needs <strong>specific amino acids</strong> (like tryptophan) to make
            serotonin.
          </li>
          <li>
            Vitamins B6, B12, iron, and magnesium impact anxiety, energy, and cognition.
          </li>
          <li>
            Gut microbiome (which is fed by <strong>your food choices</strong>) directly affects
            your <em>mind</em>.
          </li>
        </ul>
        <p>
          A recent article in <em>The Economist</em> pointed out how modern psychiatry is finally
          acknowledging what our Nani and Dadi knew all along.
        </p>
      </section>

      {/* It’s Not Just About You — It’s also about the Planet */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          It’s Not Just About You — It’s also about the Planet
        </h2>
        <p>Your burger has a backstory.</p>
        <p>
          ⚠️ Meat-heavy, industrialized diets = deforestation, water scarcity, greenhouse gases.
        </p>
        <p>
          ⚠️ Packaged food = plastic overload = landfills + oceans choking.
        </p>
        <p>Now compare that to:</p>
        <p>✅ Eating seasonal, local veggies</p>
        <p>✅ Buying grains from your neighbourhood farmer</p>
        <p>✅ Cooking at home with love and intention</p>
        <p>
          Your kitchen is a <strong>spiritual laboratory</strong>. What you choose to eat can
          either harm or heal the planet.
        </p>
      </section>

      {/* Ready to Discover What You Should Eat? */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          Ready to Discover What You Should Eat?
        </h2>
        <p>Don’t Google your next diet. Instead, go <strong>within</strong>.</p>
        <p>
          ✅ Take our <strong>FREE Prakriti Analysis Quiz</strong> and get personalized food +
          lifestyle guidance rooted in Ayurveda and psychology.
        </p>
        <Button
          text="Take the Quiz"
          size="sm"
          link="/wellness-quiz/know-your-prakriti"
          variant="primary"
        />
        <p>
          <em>(It takes 2 minutes but can change your life.)</em>
        </p>
      </section>

      {/* Final Bite of Wisdom */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">Final Bite of Wisdom</h2>
        <p>
          In a world obsessed with calories and carbs, be the rebel who eats with awareness.
        </p>
        <p>
          Let your food not just fuel your body. Let it refine your thoughts. Elevate your energy.
          And unlock your higher Self.
        </p>
        <p>
          So next time you sit for a meal, don’t just eat.{" "}
          <strong className="text-[#578d48] font-bold">
            Offer it. Bless it. Love it.
          </strong>
        </p>
      </section>

      {/* Hashtags at the Bottom */}
      <HashtagSection tags={hashtagsBottom} />
      <ShareSection/>
    </div>
  );
};

export default BlogPost;
